import { useEffect, useState } from "react";
import { t } from "ttag";

import { SettingsApi } from "metabase/services";
import {
  Box,
  Loader,
  NumberInput,
  Stack,
  Switch,
  Text,
  Title,
} from "metabase/ui";

type NumberSettingKey =
  | "cache-refresh-thread-pool-size"
  | "cache-refresh-max-per-run"
  | "cache-refresh-max-auto-refresh-configs"
  | "cache-refresh-queries-per-card"
  | "cache-refresh-run-retention-days";

type NumberSetting = {
  key: NumberSettingKey;
  label: string;
  description: string;
  min: number;
};

const getNumberSettings = (): NumberSetting[] => [
  {
    key: "cache-refresh-thread-pool-size",
    label: t`Refresh concurrency`,
    description: t`Maximum number of cache refresh queries that may run at the same time.`,
    min: 1,
  },
  {
    key: "cache-refresh-max-per-run",
    label: t`Max items per run`,
    description: t`Maximum number of configurations refreshed each time the background task runs.`,
    min: 1,
  },
  {
    key: "cache-refresh-max-auto-refresh-configs",
    label: t`Max auto-refresh configurations`,
    description: t`Maximum number of dashboards and questions that may have automatic refresh enabled.`,
    min: 0,
  },
  {
    key: "cache-refresh-queries-per-card",
    label: t`Query variations per card`,
    description: t`Number of parameterized query variations to refresh per cached card.`,
    min: 1,
  },
  {
    key: "cache-refresh-run-retention-days",
    label: t`Run history retention (days)`,
    description: t`Number of days to keep refresh run history before pruning.`,
    min: 1,
  },
];

const ENABLED_KEY = "preemptive-caching-enabled";

type SettingsState = {
  [ENABLED_KEY]: boolean;
} & Record<NumberSettingKey, number>;

const toNumber = (value: unknown, fallback: number) => {
  const parsed = typeof value === "string" ? Number(value) : (value as number);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const PreemptiveCacheSettings = () => {
  const [values, setValues] = useState<SettingsState | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    SettingsApi.list()
      .then((settings: { key: string; value: unknown }[]) => {
        if (cancelled) {
          return;
        }
        const byKey = new Map(settings.map((s) => [s.key, s.value]));
        setValues({
          [ENABLED_KEY]: byKey.get(ENABLED_KEY) !== false,
          "cache-refresh-thread-pool-size": toNumber(
            byKey.get("cache-refresh-thread-pool-size"),
            10,
          ),
          "cache-refresh-max-per-run": toNumber(
            byKey.get("cache-refresh-max-per-run"),
            100,
          ),
          "cache-refresh-max-auto-refresh-configs": toNumber(
            byKey.get("cache-refresh-max-auto-refresh-configs"),
            100,
          ),
          "cache-refresh-queries-per-card": toNumber(
            byKey.get("cache-refresh-queries-per-card"),
            10,
          ),
          "cache-refresh-run-retention-days": toNumber(
            byKey.get("cache-refresh-run-retention-days"),
            30,
          ),
        });
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const save = (key: string, value: unknown) => {
    SettingsApi.put({ key, value }).catch(() => setError(true));
  };

  if (error) {
    return <Text c="error">{t`Could not load cache refresh settings.`}</Text>;
  }

  if (!values) {
    return <Loader size="sm" />;
  }

  return (
    <Stack gap="lg" maw="40rem">
      <Box>
        <Title order={4} mb="xs">{t`Background refresh`}</Title>
        <Switch
          checked={values[ENABLED_KEY]}
          label={t`Preemptively refresh scheduled and duration caches`}
          description={t`When off, the background task stops refreshing caches for every dashboard and question.`}
          onChange={(e) => {
            const checked = e.currentTarget.checked;
            setValues({ ...values, [ENABLED_KEY]: checked });
            save(ENABLED_KEY, checked);
          }}
        />
      </Box>

      {getNumberSettings().map(({ key, label, description, min }) => (
        <NumberInput
          key={key}
          label={label}
          description={description}
          min={min}
          step={1}
          value={values[key]}
          maw="16rem"
          onChange={(value) =>
            setValues({ ...values, [key]: toNumber(value, values[key]) })
          }
          onBlur={() => save(key, values[key])}
        />
      ))}
    </Stack>
  );
};
